import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { Avatar, SkeletonRows, ErrorMessage, usePageMeta } from '../components/ui';

const pct = (x) => (x == null ? '—' : `${Number(x).toFixed(1)}%`);
const pts = (x) => (x == null ? '—' : `${x >= 0 ? '+' : ''}${Number(x).toFixed(1)}`);
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
// "2026-10-19" -> "October 19, 2026", without time zones shifting the day.
const day = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}, ${y}`;
};

function BrawlerTable({ rows, cols, navigate }) {
  return (
    <div className="tablecard">
      <div className="table-scroll">
        <table className="meta">
          <thead>
            <tr>{cols.map((c) => <th key={c.label} className={c.cls}>{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((b, i) => (
              <tr key={b.brawler_id} onClick={() => navigate(`/brawlers/${b.brawler_id}`)}>
                {cols.map((c) => <td key={c.label} className={c.cls}>{c.cell(b, i)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const brawlerCell = (b) => (
  <div className="bcell">
    <Avatar name={b.name} />
    <div className="bname">{b.name}</div>
  </div>
);

function Movers({ title, rows, field, navigate }) {
  if (!rows.length) return null;
  return (
    <div style={{ marginTop: 26 }}>
      <h2 className="section-title">{title}</h2>
      <BrawlerTable
        rows={rows}
        navigate={navigate}
        cols={[
          { label: 'Brawler', cell: brawlerCell },
          { label: 'Now', cell: (b) => <span className="num-cell">{pct(b[field])}</span> },
          { label: 'Before', cls: 'col-pick', cell: (b) => <span className="num-cell">{pct(b[`prev_${field}`])}</span> },
          {
            label: 'Change',
            cell: (b) => {
              const d = b[`${field}_change`];
              return <span className={`trend ${d >= 0 ? 'up' : 'down'}`}>{pts(d)} pts</span>;
            },
          },
        ]}
      />
    </div>
  );
}

/* What changed in the Brawl Stars meta: the last 7 days against the 7 before
   (rebuilt daily), or one finished week from the archive. Written by the
   backend from the daily rollups (routers/reports.py); prerender.py serves
   the same report as plain HTML for crawlers. */
function MetaReport() {
  const { week } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const start = data ? day(data.start) : null;
  usePageMeta({
    title: week
      ? `Brawl Stars Meta Report, Week of ${start || day(week)}`
      : 'Brawl Stars Meta This Week: Risers, Fallers & Best Picks',
    description: data ? data.summary.join(' ').slice(0, 300) : undefined,
  });

  const load = useCallback(async () => {
    setData(null);
    setError(null);
    try {
      setData(await (week ? api.getMetaReportWeek(week) : api.getMetaReport()));
    } catch (err) {
      setError(err.message);
    }
  }, [week]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="page">
      <div className="wrap">
        <div className="phead">
          <div>
            <div className="eyebrow">
              {data ? `${day(data.start)} to ${day(data.end)} · ${data.battles.toLocaleString()} battles` : 'Meta report'}
            </div>
            <h1>{week ? `Week of ${start || day(week)}` : 'This week in the meta'}</h1>
            <div className="sub">
              {data
                ? data.summary.join(' ')
                : 'What moved in the Brawl Stars meta, measured from tracked battles.'}
            </div>
          </div>
        </div>

        {error ? (
          <ErrorMessage message={error} onRetry={load} />
        ) : !data ? (
          <SkeletonRows rows={10} />
        ) : (
          <>
            {week && (
              <p className="sub" style={{ marginBottom: 18 }}>
                <Link to="/meta-report">← This week's report</Link>
              </p>
            )}

            <h2 className="section-title">Highest win rate</h2>
            <BrawlerTable
              rows={data.top_win_rate}
              navigate={navigate}
              cols={[
                { label: '#', cls: 'rank', cell: (b, i) => i + 1 },
                { label: 'Brawler', cell: brawlerCell },
                { label: 'Win %', cell: (b) => <span className="num-cell">{pct(b.win_rate)}</span> },
                { label: 'Pick %', cls: 'col-pick', cell: (b) => <span className="num-cell">{pct(b.pick_rate)}</span> },
                { label: 'Games', cls: 'col-map', cell: (b) => <span className="num-cell">{b.games.toLocaleString()}</span> },
              ]}
            />

            <Movers title="Biggest win rate gains" rows={data.risers} field="win_rate" navigate={navigate} />
            <Movers title="Biggest win rate drops" rows={data.fallers} field="win_rate" navigate={navigate} />
            <Movers title="Picked more" rows={data.pick_risers} field="pick_rate" navigate={navigate} />
            <Movers title="Picked less" rows={data.pick_fallers} field="pick_rate" navigate={navigate} />
            {data.prev_battles > 0 && !data.risers.length && !data.fallers.length && (
              <p className="sub" style={{ marginTop: 18 }}>
                No brawler's win rate moved by more than normal week-to-week noise.
              </p>
            )}

            {data.best_by_mode.length > 0 && (
              <div style={{ marginTop: 26 }}>
                <h2 className="section-title">Best brawlers by mode</h2>
                <div className="tablecard">
                  <div className="table-scroll">
                    <table className="meta">
                      <thead><tr><th>Mode</th><th>Top picks</th></tr></thead>
                      <tbody>
                        {data.best_by_mode.map((m) => (
                          <tr key={m.mode}>
                            <td><strong>{m.label}</strong></td>
                            <td>
                              {m.top.map((b, i) => (
                                <span key={b.brawler_id}>
                                  {i > 0 && ', '}
                                  <Link to={`/brawlers/${b.brawler_id}`}>{b.name}</Link> ({pct(b.win_rate)})
                                </span>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {data.weeks.length > 0 && (
              <div style={{ marginTop: 26 }}>
                <h2 className="section-title">Past weekly reports</h2>
                <div className="filters">
                  {data.weeks.map((w) => (
                    <Link key={w} to={`/meta-report/${w}`} className={`pill-chip ${w === week ? 'lime' : ''}`}>
                      {day(w)}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <p className="sub" style={{ marginTop: 22 }}>
              Brawlers need {data.min_games.toLocaleString()} games to be ranked. Gains and drops are only
              listed when the change is bigger than random variation would explain.
              {' '}Brawlers that moved get their own write-up in <Link to="/news">the news</Link>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default MetaReport;
